/**
 * @file ProductReviewsList.tsx
 * @description Тело списка очереди модерации (phase19.10): лесенка
 * загрузка → пусто → карточки + футер серверной пагинации.
 */

import { Loader2, MessageSquareOff } from "lucide-react";

import { EditorialCard } from "../../../components/ui/EditorialCard";
import { ListPagination } from "../../../components/ui/ListPagination";
import type {
  AdminProductReview,
  ProductReviewStatus,
} from "../../../lib/api-product-reviews";
import { ProductReviewCard } from "./ProductReviewCard";

interface ProductReviewsListProps {
  items: AdminProductReview[];
  loading: boolean;
  status: ProductReviewStatus;
  shopSlug: string;
  total: number;
  /** Текущая страница, 1-based (в ListPagination уходит как pageIndex). */
  page: number;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  onPrev: () => void;
  onNext: () => void;
  onChanged: () => void;
}

export function ProductReviewsList({
  items,
  loading,
  status,
  shopSlug,
  total,
  page,
  pageSize,
  onPageSizeChange,
  onPrev,
  onNext,
  onChanged,
}: ProductReviewsListProps) {
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="size-6 animate-spin text-on-surface-variant" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <EditorialCard>
        <EditorialCard.Body>
          <div className="flex flex-col items-center gap-3 py-12 text-center">
            <MessageSquareOff className="size-10 text-on-surface-variant" />
            <h2 className="text-lg font-semibold">Отзывов нет</h2>
            <p className="text-sm text-on-surface-variant">
              {status === "PENDING"
                ? "Очередь модерации пуста — новые отзывы появятся здесь."
                : "По выбранным фильтрам ничего не найдено."}
            </p>
          </div>
        </EditorialCard.Body>
      </EditorialCard>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-3">
        {items.map((r) => (
          <ProductReviewCard
            key={r.id}
            review={r}
            shopSlug={shopSlug}
            onChanged={onChanged}
          />
        ))}
      </div>
      <ListPagination
        total={total}
        pageIndex={page - 1}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
        onPrev={onPrev}
        onNext={onNext}
        canPrev={page > 1}
        canNext={page * pageSize < total}
      />
    </>
  );
}
